import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { LangContext } from "../contexts/LangContext";
import { AvailableLocales, availableLocales, loadCatalog } from "../i18n";
import { I18nProvider } from "@lingui/react";
import { i18n } from "@lingui/core";

export interface LangProviderProps extends PropsWithChildren {
  lsKey?: string;
}

export const LangProvider = ({
  children,
  lsKey = "selected-language",
}: LangProviderProps) => {
  const [currentLang, setCurrentLang] = useState<AvailableLocales>("en");

  async function getCurrentLang() {
    let lang = getLangFromLocalStorage();
    if (!lang) lang = getLangFromNavigator();
    if (!availableLocales.includes(lang as any)) lang = "en";
    return lang as AvailableLocales;
  }

  const getLangFromLocalStorage = useCallback(() => {
    const lang = localStorage.getItem(lsKey);
    if (!lang) return null;
    return lang;
  }, [lsKey]);

  function getLangFromNavigator() {
    return navigator.language.split("-")[0];
  }

  useEffect(() => {
    getCurrentLang().then((lang) => setCurrentLang(lang));
  }, []);

  const setLangToLocalStorage = useCallback(
    (lang: AvailableLocales) => {
      localStorage.setItem(lsKey, lang);
    },
    [lsKey]
  );

  useEffect(() => {
    loadCatalog(currentLang);
  }, [currentLang]);

  function onLangSet(lang: AvailableLocales) {
    setLangToLocalStorage(lang);
    setCurrentLang(lang);
  }

  return (
    <LangContext.Provider value={{ currentLang, setCurrentLang: onLangSet }}>
      <I18nProvider i18n={i18n as any}>{children}</I18nProvider>
    </LangContext.Provider>
  );
};
